import { useState, useRef, useMemo, useCallback, useEffect } from 'react';
import { useSelector } from 'react-redux';
import { ClipboardDocumentListIcon } from '../../automationStudioIcons';
import layoutStyles from '../../../../styles/automationStudio/automationStudio.module.scss';
import styles from '../../../../styles/automationStudio/automationStudio.module.scss';
import { LABELS } from '../../content';
import PILComponent from '../../../common/PILComponent';
import SavedPlanLoadingState from './loading/SavedPlanLoadingState';
import InstructionsEmptyState from './sections/InstructionsEmptyState';
import FunctionLibrarySection from './sections/FunctionLibrarySection';
import ParametersSection from './sections/ParametersSection';
import InstructionHierarchy from './steps/InstructionHierarchy';
import { useResizableSection } from './hooks/useResizableSection';
import { selectHasUnsavedChanges } from '../../../../features/automationStudioNew/automationStudioNewSelectors';

export default function LeftPanel({
    instructions = [],
    isGenerating = false,
    isLoadingSavedPlan = false,
    isRecording = false,
    selectedStep = null,
    selectedStepIds = [],
    isRunning = false,
    isReplayExecuting = false,
    isReplayFlow = false,
    isLoadedFromTestcase = false,
    usesLeftPanelGroupSnapshot = false,
    hasExecutedInSession = false,
    replayProgressMap = {},
    replayExecutionResults = null,
    replayCurrentStepIndex = null,
    executionFlow = null,
    fileSuggestions = [],
    variablesList = [],
    variablesListDescriptions = {},
    instructionHeadingsSnapshot = {},
    stepValidationErrors = {},
    canCopy = false,
    canPaste = false,
    onStepSelect,
    onInstructionSelect,
    onAddStep = () => {},
    onStepIdSelect,
    onCopy,
    onPasteAbove,
    onPasteBelow,
    onContextMenuOpen,
    onPasteApplied,
    onKeyCopyPaste,
    children,
}) {
    const hasUnsavedChanges = useSelector(selectHasUnsavedChanges);
    const [isFunctionLibraryExpanded, setIsFunctionLibraryExpanded] = useState(false);
    const [isParametersExpanded, setIsParametersExpanded] = useState(false);
    const panelRef = useRef(null);

    const {
        functionLibraryHeight,
        parametersHeight,
        handleResizeStart,
        minInstructionsHeight,
    } = useResizableSection(panelRef, {
        isFunctionLibraryExpanded,
        isParametersExpanded,
    });

    const instructionsWithStepsCount = useMemo(
        () => instructions.filter((inst) => inst.aiSteps && inst.aiSteps.length > 0).length,
        [instructions]
    );
    const hasInstructionsWithSteps = instructionsWithStepsCount > 0;

    const onKeyDown = useCallback(
        (e) => {
            const target = e.target;
            if (
                target.tagName === 'INPUT' ||
                target.tagName === 'TEXTAREA' ||
                target.isContentEditable
            ) {
                return;
            }
            onKeyCopyPaste?.(e);
        },
        [onKeyCopyPaste]
    );

    useEffect(() => {
        const panel = panelRef.current;
        if (!panel) return undefined;
        panel.addEventListener('keydown', onKeyDown);
        return () => panel.removeEventListener('keydown', onKeyDown);
    }, [onKeyDown]);

    const hierarchyContent = children ?? (
        <InstructionHierarchy
            instructions={instructions}
            selectedStep={selectedStep}
            selectedStepIds={selectedStepIds}
            isRunning={isRunning}
            isGenerating={isGenerating}
            isReplayExecuting={isReplayExecuting}
            isReplayFlow={isReplayFlow}
            isLoadedFromTestcase={isLoadedFromTestcase}
            usesLeftPanelGroupSnapshot={usesLeftPanelGroupSnapshot}
            hasExecutedInSession={hasExecutedInSession}
            replayProgressMap={replayProgressMap}
            replayExecutionResults={replayExecutionResults}
            replayCurrentStepIndex={replayCurrentStepIndex}
            executionFlow={executionFlow}
            fileSuggestions={fileSuggestions}
            variablesList={variablesList}
            variablesListDescriptions={variablesListDescriptions}
            instructionHeadingsSnapshot={instructionHeadingsSnapshot}
            stepValidationErrors={stepValidationErrors}
            canCopy={canCopy}
            canPaste={canPaste}
            onInstructionSelect={onInstructionSelect}
            onStepSelect={onStepSelect}
            onAddStep={onAddStep}
            onStepIdSelect={onStepIdSelect}
            onCopy={onCopy}
            onPasteAbove={onPasteAbove}
            onPasteBelow={onPasteBelow}
            onContextMenuOpen={onContextMenuOpen}
        />
    );

    return (
        <div
            ref={panelRef}
            className={layoutStyles.leftPanel}
            tabIndex={0}
            data-testid="left-panel-root"
        >
            <div
                className={styles.leftPanelSection}
                style={{ flex: 1, minHeight: `${minInstructionsHeight}px` }}
            >
                <div className={styles.sectionHeader}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                        <ClipboardDocumentListIcon className={styles.sectionIcon} />
                        <span className={`${styles.sectionTitle} ${styles.asTypoSubtitle2}`}>
                            {LABELS.INSTRUCTIONS_AND_STEPS}
                        </span>
                    </div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                        {hasUnsavedChanges && (
                            <PILComponent
                                size="medium"
                                label={LABELS.UNSAVED_CHANGES}
                                type="warning"
                            />
                        )}
                        {hasInstructionsWithSteps && (
                            <div className={styles.sectionBadge}>
                                {instructionsWithStepsCount}
                            </div>
                        )}
                    </div>
                </div>
                <div className={styles.sectionContent}>
                    {isLoadingSavedPlan ? (
                        <SavedPlanLoadingState />
                    ) : (hasInstructionsWithSteps || isGenerating) ? (
                        hierarchyContent
                    ) : (
                        <InstructionsEmptyState />
                    )}
                </div>
            </div>

            <FunctionLibrarySection
                isExpanded={isFunctionLibraryExpanded}
                onToggle={() => setIsFunctionLibraryExpanded(!isFunctionLibraryExpanded)}
                height={functionLibraryHeight}
                onResizeStart={(e) => handleResizeStart('function', e)}
            />

            <ParametersSection
                isExpanded={isParametersExpanded}
                onToggle={() => setIsParametersExpanded(!isParametersExpanded)}
                height={parametersHeight}
                onResizeStart={(e) => handleResizeStart('parameters', e)}
            />
        </div>
    );
}
